import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Lightbulb, ArrowRight, Mail, Lock, User, KeyRound, Sparkles, Gift } from "lucide-react";

export default function Auth() {
  const { user, ready, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("register") !== null ? "register" : "login");
  const [regType, setRegType] = useState("trial"); // trial | license
  const [form, setForm] = useState({ name: "", email: "", password: "", license: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/dashboard");
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Willkommen zurück!");
      } else {
        const licenseKey = regType === "license" ? form.license : "";
        await register(form.name, form.email, form.password, licenseKey);
        toast.success(regType === "license" ? "Konto erstellt – los geht's!" : "Gratis-Konto erstellt – Modul 1 wartet!");
      }
      navigate("/dashboard");
    } catch (err) {
      const msg = (!err.response || err.response.status >= 500)
        ? "Server momentan nicht erreichbar. Bitte in wenigen Sekunden erneut versuchen."
        : (formatApiError(err.response?.data?.detail) || err.message);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-5 py-10">
      <div className="absolute inset-0 radial-fade -z-10" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <div className="relative">
            <Lightbulb className="h-7 w-7 text-gold" />
            <div className="absolute inset-0 blur-md bg-gold/40 rounded-full -z-10 animate-pulse-glow" />
          </div>
          <div className="leading-none">
            <span className="font-heading font-extrabold text-xl">LUX</span>
            <span className="block text-[9px] uppercase tracking-[0.25em] text-muted-foreground">Lighting Academy</span>
          </div>
        </Link>

        <div className="rounded-2xl glass-strong p-7 sm:p-8">
          <div className="flex gap-1 p-1 rounded-full bg-white/5 mb-7">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                data-testid={`auth-tab-${m}`}
                className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  mode === m ? "bg-gold text-[#0a0d12]" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {m === "login" ? "Anmelden" : "Registrieren"}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRegType("trial")}
                  data-testid="auth-regtype-trial"
                  className={`flex flex-col items-start gap-1 p-3.5 rounded-xl border text-left transition-all duration-300 ${
                    regType === "trial" ? "bg-gold/15 border-gold/40" : "bg-white/5 border-white/10 hover:bg-white/[0.08]"
                  }`}
                >
                  <Gift className={`h-4 w-4 ${regType === "trial" ? "text-gold" : "text-muted-foreground"}`} />
                  <span className="text-sm font-medium">Gratis testen</span>
                  <span className="text-[11px] text-muted-foreground leading-tight">Modul 1 · kein Schlüssel nötig</span>
                </button>
                <button
                  type="button"
                  onClick={() => setRegType("license")}
                  data-testid="auth-regtype-license"
                  className={`flex flex-col items-start gap-1 p-3.5 rounded-xl border text-left transition-all duration-300 ${
                    regType === "license" ? "bg-gold/15 border-gold/40" : "bg-white/5 border-white/10 hover:bg-white/[0.08]"
                  }`}
                >
                  <Sparkles className={`h-4 w-4 ${regType === "license" ? "text-gold" : "text-muted-foreground"}`} />
                  <span className="text-sm font-medium">Vollzugang</span>
                  <span className="text-[11px] text-muted-foreground leading-tight">Mit Lizenzschlüssel</span>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field icon={User} label="Name">
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ihr Name"
                  data-testid="auth-name-input"
                  className="w-full bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60"
                />
              </Field>
            )}
            {mode === "register" && regType === "license" && (
              <Field icon={KeyRound} label="Lizenzschlüssel">
                <input
                  type="text"
                  required
                  value={form.license}
                  onChange={(e) => setForm({ ...form, license: e.target.value })}
                  placeholder="LUX-XXXXX-XXXXX-XXXXX"
                  data-testid="auth-license-input"
                  className="w-full bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60 uppercase tracking-wider"
                />
              </Field>
            )}
            <Field icon={Mail} label="E-Mail">
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@buero.ch"
                data-testid="auth-email-input"
                className="w-full bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>
            <Field icon={Lock} label="Passwort">
              <input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                data-testid="auth-password-input"
                className="w-full bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>

            {mode === "register" && regType === "trial" && (
              <p className="text-xs text-foreground/60 leading-relaxed flex items-start gap-2" data-testid="auth-trial-note">
                <Gift className="h-3.5 w-3.5 text-gold flex-shrink-0 mt-0.5" />
                Mit dem Gratis-Konto erkunden Sie Modul 1 vollständig und können den KI-Coach begrenzt
                ausprobieren. Den vollen Kurs schalten Sie jederzeit mit einem Lizenzschlüssel frei.
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60"
            >
              {loading
                ? "Bitte warten…"
                : mode === "login"
                ? "Anmelden"
                : regType === "trial"
                ? "Gratis-Konto erstellen"
                : "Konto erstellen"}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1.5 flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus-within:border-gold/50 focus-within:ring-2 focus-within:ring-gold/20 transition-all">
        <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        {children}
      </div>
    </label>
  );
}
